import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showClearButton?: boolean;
}

export function SearchInput({
  value,
  onChange,
  placeholder = 'Search',
  className,
  size = 'sm',
  showClearButton = true,
}: SearchInputProps) {
  const sizeClasses = {
    sm: 'h-7 text-xs w-32',
    md: 'h-8 text-sm w-48',
    lg: 'h-9 text-sm w-64',
  };

  const iconSizes = {
    sm: 12,
    md: 14,
    lg: 16,
  };

  return (
    <div
      className={cn(
        'bg-muted px-2 py-1 rounded-md border border-transparent focus-within:border-primary flex items-center gap-1.5 transition-colors',
        sizeClasses[size],
        className
      )}
    >
      <Search size={iconSizes[size]} className="text-muted-foreground shrink-0" />
      <input
        type="text"
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border-none outline-none text-foreground w-full placeholder:text-muted-foreground"
      />
      {showClearButton && value && (
        <button
          onClick={() => onChange('')}
          aria-label="Clear search"
          className="p-0.5 rounded hover:bg-accent transition-colors shrink-0"
        >
          <X size={iconSizes[size]} className="text-muted-foreground" />
        </button>
      )}
    </div>
  );
}

// Helper function to highlight search matches in text
export function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');
  const parts = text.split(regex);

  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} className="bg-yellow-300 dark:bg-yellow-500/70 text-foreground rounded px-0.5">
        {part}
      </mark>
    ) : (
      part
    )
  );
}

// Helper function to highlight search matches in HTML string
export function highlightHtml(html: string, query: string): string {
  if (!query.trim()) return html;

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`(${escapedQuery})`, 'gi');

  // Split HTML by tags to avoid highlighting inside tags
  const parts = html.split(/(<[^>]*>)/);

  return parts
    .map((part) => {
      // If it's a tag, don't modify it
      if (part.startsWith('<')) return part;
      // Otherwise, highlight matches
      return part.replace(
        regex,
        '<mark class="bg-yellow-300 dark:bg-yellow-500/70 text-foreground rounded px-0.5">$1</mark>'
      );
    })
    .join('');
}
